import { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, FolderInput, Loader2, Paperclip, ShieldAlert, ShieldCheck, ShieldQuestion, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { commsFilesAPI, errMsg, blobErrMsg, fmtBytes } from '../../lib/communicationsAPI';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.heic,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt';
const PREVIEWABLE = (t) => t === 'application/pdf' || (t?.startsWith('image/') && t !== 'image/heic');

const ScanBadge = ({ status }) => {
  const m = { clean: [ShieldCheck, 'text-emerald-600', 'Scanned'], pending: [ShieldQuestion, 'text-amber-600', 'Scan pending'],
    error: [ShieldQuestion, 'text-amber-600', 'Awaiting scan — download blocked'], infected: [ShieldAlert, 'text-red-600', 'Infected — blocked'],
    blocked: [ShieldAlert, 'text-red-600', 'Type not allowed — metadata only'], not_fetched: [ShieldQuestion, 'text-muted-foreground', 'Fetched & scanned on download'] }[status] || [];
  const [Icon, cls, label] = m;
  return Icon ? <span className={cn('inline-flex items-center gap-1 text-[10px]', cls)} title={label}><Icon className="w-3 h-3" />{label}</span> : null;
};

// Paperclip button + pending upload chips (scanned on upload). value: [{id, filename, size, scan_status}]
export const AttachmentPicker = ({ value, onChange, conversationId, disabled }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (value.length + files.length > 10) { toast.error('You can attach up to 10 files per message'); return; }
    setBusy(true);
    try {
      const res = await commsFilesAPI.upload(files, conversationId);
      onChange([...value, ...res.data.attachments]);
      res.data.rejected.forEach((r) => toast.error(`${r.filename}: ${r.reason}`));
    } catch (err) { toast.error(errMsg(err, 'Upload failed')); } finally { setBusy(false); }
  };
  const remove = (a) => { commsFilesAPI.remove(a.id).catch(() => {}); onChange(value.filter((x) => x.id !== a.id)); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="attachment-picker">
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={pick} data-testid="attachment-file-input" />
      <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => input.current?.click()} disabled={disabled || busy}
        aria-label="Attach files" data-testid="attachment-add-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
      </Button>
      {value.map((a) => (
        <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 pl-2.5 pr-1 py-0.5 text-xs" data-testid={`pending-attachment-${a.id}`}>
          <FileText className="w-3 h-3" />{a.filename}<span className="text-muted-foreground">{fmtBytes(a.size)}</span><ScanBadge status={a.scan_status} />
          <button type="button" onClick={() => remove(a)} aria-label={`Remove ${a.filename}`} className="rounded-full p-0.5 hover:bg-muted"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
};

const PreviewDialog = ({ att, onClose }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    commsFilesAPI.blob(att.id, true).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(async (e) => { toast.error(await blobErrMsg(e, 'Preview unavailable')); onClose(); });
    return () => u && URL.revokeObjectURL(u);
  }, [att.id]); // eslint-disable-line
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="attachment-preview-dialog">
        <DialogHeader><DialogTitle className="truncate">{att.filename}</DialogTitle></DialogHeader>
        {!url ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-12" /> : att.content_type === 'application/pdf'
          ? <iframe src={url} title={att.filename} className="w-full h-[70vh] rounded border" data-testid="attachment-preview-pdf" />
          : <img src={url} alt={att.filename} className="max-h-[70vh] mx-auto rounded" data-testid="attachment-preview-image" />}
      </DialogContent>
    </Dialog>
  );
};

const SaveDialog = ({ att, onClose, onSaved }) => {
  const [visibility, setVisibility] = useState('staff_only');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await commsFilesAPI.saveToDocuments(att.id, visibility);
      toast.success(`Saved to client Documents (version ${r.data.version})`);
      onSaved(r.data.document_id); onClose();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="attachment-save-dialog">
        <DialogHeader>
          <DialogTitle>Save to Documents</DialogTitle>
          <DialogDescription>{att.filename} will be added to the client's Documents. Same-name files become a new version.</DialogDescription>
        </DialogHeader>
        <Select value={visibility} onValueChange={setVisibility}>
          <SelectTrigger data-testid="attachment-save-visibility"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="staff_only">Staff only</SelectItem>
            <SelectItem value="client_visible">Visible to client</SelectItem>
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="attachment-save-confirm">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Attachments on a sent message: preview (PDF/images), download, and (staff) Save to Documents.
export const AttachmentList = ({ items, staff, inverted }) => {
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState({});
  if (!items?.length) return null;
  const download = async (a) => {
    try {
      const r = await commsFilesAPI.blob(a.id);
      const u = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: u, download: a.filename }).click();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch (e) { toast.error(await blobErrMsg(e, 'Download failed')); }
  };
  return (
    <div className="mt-2 space-y-1">
      {items.map((a) => {
        const ok = a.scan_status === 'clean';
        return (
          <div key={a.id} className={cn('flex items-center gap-2 rounded-md px-2 py-1 text-xs', inverted ? 'bg-primary-foreground/15' : 'bg-background/70 border border-border')} data-testid={`attachment-${a.id}`}>
            <FileText className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate font-medium">{a.filename}</span>
            <span className="opacity-70 shrink-0">{fmtBytes(a.size)}</span>
            {!ok && <ScanBadge status={a.scan_status} />}
            <span className="ml-auto flex items-center gap-0.5 shrink-0">
              {ok && PREVIEWABLE(a.content_type) && <button type="button" onClick={() => setPreview(a)} className="p-1 rounded hover:bg-black/10" aria-label={`Preview ${a.filename}`} data-testid={`attachment-preview-${a.id}`}><Eye className="w-3.5 h-3.5" /></button>}
              {(ok || (a.scan_status === 'not_fetched' && a.source === 'email')) && <button type="button" onClick={() => download(a)} className="p-1 rounded hover:bg-black/10" aria-label={`Download ${a.filename}`} data-testid={`attachment-download-${a.id}`}><Download className="w-3.5 h-3.5" /></button>}
              {ok && staff && (a.document_id || saved[a.id]
                ? <span className="text-[10px] opacity-80 px-1" data-testid={`attachment-saved-${a.id}`}>In Documents</span>
                : <button type="button" onClick={() => setSaving(a)} className="p-1 rounded hover:bg-black/10" aria-label="Save to Documents" title="Save to Documents" data-testid={`attachment-save-${a.id}`}><FolderInput className="w-3.5 h-3.5" /></button>)}
            </span>
          </div>
        );
      })}
      {preview && <PreviewDialog att={preview} onClose={() => setPreview(null)} />}
      {saving && <SaveDialog att={saving} onClose={() => setSaving(null)} onSaved={(id) => setSaved((s) => ({ ...s, [saving.id]: id }))} />}
    </div>
  );
};
